import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input } from '@arco-design/web-react';

import {
  NetworkSelectField,
  parseNetworkSelectDisplayName,
  useNetworkSecurityGroupOptions,
  useNetworkVpcOptions,
} from '../../components/networkSelectField';
import type { CapabilityControlProps } from '../../core/types';
import { isFieldInteractionLocked, isFieldReadonly } from '../../platform/capability-registry/fieldControl';
import { isFieldVisible } from '../../platform/capability-registry/resolveFieldMode';
import { shouldSkipCapabilityFetch } from '../../platform/capability-registry/shouldSkipCapabilityFetch';
import { NETWORK_FIELD_KEYS, NETWORK_OPTION_META_KEYS } from './fieldKeys';
import styles from './index.module.less';
import type { NetworkBlockProps, NetworkPaths } from './types';

const FormItem = Form.Item;

function buildNetworkFetchGate(control: CapabilityControlProps, skipFetch?: boolean) {
  return {
    vpc: shouldSkipCapabilityFetch(control, { skipFetch, fieldKeys: [NETWORK_FIELD_KEYS.vpc] }),
    securitygroup: shouldSkipCapabilityFetch(control, { skipFetch, fieldKeys: [NETWORK_FIELD_KEYS.securitygroup] }),
  };
}

/** 网络配置能力块（VPC / 子网 / 安全组 / 可选静态 IP） */
export function NetworkBlock(props: NetworkBlockProps) {
  const { form, paths: rawPaths, options, linkageProps, ...control } = props;
  const paths = rawPaths as NetworkPaths;
  const { t } = useTranslation();

  const showSecurityGroup = options?.showSecurityGroup !== false;
  const showSubnet = options?.showSubnet !== false;
  const showDisplayNameFields = options?.showDisplayNameFields === true;

  const workspace = linkageProps?.workspace ?? options?.workspace;
  const datacenter = linkageProps?.datacenter ?? options?.datacenter;

  const provisioningMode = Form.useWatch('provisioning.mode', form) as string | undefined;
  const showStaticIpv4 = options?.showStaticIpv4 === true || (options?.showStaticIpv4WhenAdopt === true && provisioningMode === 'Adopt');
  const staticIpv4FromNode = Form.useWatch('_staticIpv4FromNode', form) === true;

  const vpcValue = Form.useWatch(paths.vpc, form) as string | undefined;
  const prevWorkspaceRef = useRef<string | undefined>();

  const networkQuery = useMemo(
    () => ({
      ws: workspace?.trim() || undefined,
      dc: datacenter?.trim() || undefined,
    }),
    [datacenter, workspace],
  );

  const skipListFetch = useMemo(
    () => buildNetworkFetchGate(control, options?.skipFetch),
    [control.blockMode, control.fieldModes, options?.skipFetch],
  );

  const { options: vpcs, isLoading: vpcLoading } = useNetworkVpcOptions({
    ws: networkQuery.ws,
    dc: networkQuery.dc,
    enabled: !skipListFetch.vpc,
  });

  const { options: securityGroups, isLoading: sgLoading } = useNetworkSecurityGroupOptions({
    vpc: vpcValue,
    ws: networkQuery.ws,
    dc: networkQuery.dc,
    enabled: !skipListFetch.securitygroup,
  });

  useEffect(() => {
    const prev = prevWorkspaceRef.current;
    prevWorkspaceRef.current = workspace;
    if (prev !== undefined && prev !== workspace) {
      form.setFieldValue(paths.vpc, undefined);
      form.setFieldValue(paths.securitygroup, undefined);
      if (showDisplayNameFields && paths.vpcDisplayName) {
        form.setFieldValue(paths.vpcDisplayName, undefined);
      }
      if (showDisplayNameFields && paths.securityGroupDisplayName) {
        form.setFieldValue(paths.securityGroupDisplayName, undefined);
      }
    }
  }, [form, paths, showDisplayNameFields, workspace]);

  useEffect(() => {
    if (vpcValue === '') {
      form.setFieldValue(paths.vpc, undefined);
    }
  }, [form, paths.vpc, vpcValue]);

  const sgValue = Form.useWatch(paths.securitygroup, form) as string | undefined;

  useEffect(() => {
    if (sgValue === '') {
      form.setFieldValue(paths.securitygroup, undefined);
    }
  }, [form, paths.securitygroup, sgValue]);

  const handleVpcChange = useCallback(
    (val: string | undefined, option?: unknown) => {
      if (val && showDisplayNameFields && paths.vpcDisplayName) {
        const displayName = parseNetworkSelectDisplayName(option);
        form.setFieldsValue({
          [paths.subnet]: val,
          [paths.vpcDisplayName]: displayName || val || '-',
        });
      }

      form.setFieldValue(paths.securitygroup, undefined);
      if (showDisplayNameFields && paths.securityGroupDisplayName) {
        form.setFieldValue(paths.securityGroupDisplayName, undefined);
      }
    },
    [form, paths, showDisplayNameFields],
  );

  const handleSecurityGroupChange = useCallback(
    (val: string | undefined, option?: unknown) => {
      if (!showDisplayNameFields || !paths.securityGroupDisplayName) {
        return;
      }
      const displayName = parseNetworkSelectDisplayName(option);
      form.setFieldsValue({
        [paths.securitygroup]: val,
        [paths.securityGroupDisplayName]: displayName || val || '-',
      });
    },
    [form, paths, showDisplayNameFields],
  );

  const vpcLocked = isFieldInteractionLocked(NETWORK_FIELD_KEYS.vpc, control);
  const securityGroupLocked = isFieldInteractionLocked(NETWORK_FIELD_KEYS.securitygroup, control);
  const staticIpv4Locked = staticIpv4FromNode || isFieldInteractionLocked(NETWORK_FIELD_KEYS.staticIpv4, control);
  const vpcSelectDisabled = vpcLocked || skipListFetch.vpc;
  const securityGroupSelectDisabled = securityGroupLocked || skipListFetch.securitygroup;

  const staticIpv4Label = t('data-center:static.ipv4.address');
  /** Adopt 展示时必填；有值再校验 IPv4 格式 */
  const staticIpv4Rules = useMemo(() => {
    if (!showStaticIpv4 || control.blockMode === 'preview') return undefined;
    return [
      {
        required: true,
        message: t('common:validate.required', { name: staticIpv4Label }),
      },
      {
        validator(value: string | undefined, callback: (error?: string) => void) {
          if (!value) {
            callback();
            return;
          }
          const ipv4Regex = /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)$/;
          if (!ipv4Regex.test(value)) {
            callback(t('data-center:please.enter.a.valid.ipv4.address.2'));
            return;
          }
          callback();
        },
      },
    ];
  }, [control.blockMode, showStaticIpv4, staticIpv4Label, t]);

  if (
    !isFieldVisible(NETWORK_FIELD_KEYS.vpc, control) &&
    !isFieldVisible(NETWORK_FIELD_KEYS.securitygroup, control) &&
    !isFieldVisible(NETWORK_FIELD_KEYS.staticIpv4, control)
  ) {
    return null;
  }

  return (
    <>
      {showSubnet && isFieldVisible(NETWORK_FIELD_KEYS.subnet, control) ? (
        <FormItem label={t('data-center:subnet')} field={paths.subnet} hidden initialValue="default" className={styles.hiddenField}>
          <Input />
        </FormItem>
      ) : null}

      {showDisplayNameFields && paths.vpcDisplayName ? (
        <FormItem field={paths.vpcDisplayName} hidden className={styles.hiddenField}>
          <Input />
        </FormItem>
      ) : null}

      {showDisplayNameFields && paths.securityGroupDisplayName ? (
        <FormItem field={paths.securityGroupDisplayName} hidden className={styles.hiddenField}>
          <Input />
        </FormItem>
      ) : null}

      {isFieldVisible(NETWORK_FIELD_KEYS.vpc, control) ? (
        <NetworkSelectField
          form={form}
          field={paths.vpc}
          label="VPC"
          options={vpcs}
          loading={vpcLoading}
          disabled={vpcSelectDisabled}
          required={!vpcLocked}
          requiredMessage={t('common:required.field')}
          placeholder={t('data-center:please.select.a.vpc')}
          metaSyncEnabled={!skipListFetch.vpc}
          onChange={handleVpcChange}
          metaKey={NETWORK_OPTION_META_KEYS.vpc}
        />
      ) : null}

      {showSecurityGroup && isFieldVisible(NETWORK_FIELD_KEYS.securitygroup, control) ? (
        <NetworkSelectField
          form={form}
          field={paths.securitygroup}
          label={t('common:security.group')}
          options={securityGroups}
          loading={sgLoading}
          disabled={securityGroupSelectDisabled}
          placeholder={t('cluster:select-security-group')}
          metaSyncEnabled={!skipListFetch.securitygroup}
          onChange={handleSecurityGroupChange}
          metaKey={NETWORK_OPTION_META_KEYS.securitygroup}
        />
      ) : null}

      {paths.staticIpv4 ? (
        <FormItem
          hidden={!showStaticIpv4}
          label={staticIpv4Label}
          field={paths.staticIpv4}
          disabled={staticIpv4Locked}
          rules={staticIpv4Rules}
        >
          <Input
            placeholder={t('data-center:ipv4.address')}
            disabled={staticIpv4Locked}
            readOnly={isFieldReadonly(NETWORK_FIELD_KEYS.staticIpv4, control)}
          />
        </FormItem>
      ) : null}
    </>
  );
}
